import type { RemittancePaymentDetail } from '../../../services/api/types.gen';
import {
  formatRemittanceCurrency,
  formatRemittanceDate,
  formatRemittanceDateRange,
} from './remittanceFormatters';

/**
 * The facts one incoming 835 payment carries, shown beside the candidate list
 * so the biller can match the advice to a claim instead of picking blind.
 *
 * Every value is read straight off the source payment the detail endpoint
 * already returned: nothing here comes from the candidate the biller is
 * choosing, so selecting a claim cannot rewrite the advice's own identity and
 * dates. Only source fields are shown: the stored claim-level statement
 * dates are not part of this payload.
 */

/** Both name parts when the advice carries them, so either can be absent. */
const formatSourcePatient = (payment: RemittancePaymentDetail): string =>
  [payment.patient_first_name, payment.patient_last_name]
    .filter(Boolean)
    .join(' ') || '—';

/**
 * One label per source service line: the line's own date when the 835 carried
 * a single date, otherwise that line's range. Disjoint lines stay separate —
 * folding them into one range would claim a period the advice never stated.
 * Repeated labels collapse so a repeated line does not read as another date.
 */
const formatServiceLineDates = (payment: RemittancePaymentDetail): string => {
  const labels = new Set<string>();
  for (const line of payment.service_lines ?? []) {
    const label = line.service_date
      ? formatRemittanceDate(line.service_date)
      : formatRemittanceDateRange(
          line.service_start_date,
          line.service_end_date
        );
    // A blank label is a line with no usable date, or only a malformed one.
    if (label && label !== '—') labels.add(label);
  }
  return labels.size > 0 ? Array.from(labels).join(', ') : 'Not provided';
};

export function RemittanceClaimContext({
  payment,
}: {
  payment: RemittancePaymentDetail;
}) {
  return (
    <section
      aria-label='Remittance claim'
      className='rounded-md border bg-muted/30 p-3'
    >
      <h3 className='mb-2 text-sm font-medium'>Remittance claim</h3>
      <dl className='grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2'>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>Patient</dt>
          <dd className='break-words text-sm'>
            {formatSourcePatient(payment)}
          </dd>
        </div>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>Patient control #</dt>
          <dd className='break-words text-sm'>
            {payment.patient_control_number || '—'}
          </dd>
        </div>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>
            Payer claim control #
          </dt>
          <dd className='break-words text-sm'>
            {payment.payer_claim_control_number || '—'}
          </dd>
        </div>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>Service-line dates</dt>
          {/* A long list scrolls here rather than burying the picker. */}
          <dd className='max-h-24 overflow-y-auto break-words text-sm'>
            {formatServiceLineDates(payment)}
          </dd>
        </div>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>Charge</dt>
          <dd className='break-words text-sm'>
            {formatRemittanceCurrency(payment.total_claim_charge_amount)}
          </dd>
        </div>
        <div className='min-w-0'>
          <dt className='text-xs text-muted-foreground'>Paid</dt>
          <dd className='break-words text-sm'>
            {formatRemittanceCurrency(payment.claim_payment_amount)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default RemittanceClaimContext;
